import * as React from "react"
import { format, parse } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export function DatePicker({ 
  value, 
  onChange, 
  placeholder = "Pick a date",
  disabled = false,
  className,
  dateFormat = "dd/MM/yyyy",
  defaultMonth,
  ...props
}) {
  const [open, setOpen] = React.useState(false)
  
  // Parse the string value to Date if it's in DD/MM/YYYY format
  const dateValue = React.useMemo(() => {
    if (!value) return undefined
    try {
      // Try to parse DD/MM/YYYY format
      const parsed = parse(value, "dd/MM/yyyy", new Date())
      if (!isNaN(parsed.getTime())) {
        return parsed
      }
    } catch {
      // Ignore parse errors
    }
    return undefined
  }, [value])

  // Month the calendar opens on when no value is selected (e.g. predicted use-by month)
  const defaultMonthDate = React.useMemo(() => {
    if (!defaultMonth) return undefined
    if (defaultMonth instanceof Date) return defaultMonth
    try {
      const parsed = parse(defaultMonth, "dd/MM/yyyy", new Date())
      if (!isNaN(parsed.getTime())) return parsed
    } catch {
      // Ignore parse errors
    }
    return undefined
  }, [defaultMonth])

  const handleSelect = (date) => {
    if (date) {
      onChange(format(date, dateFormat))
    } else {
      onChange("")
    }
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-mono h-9",
            !value && "text-muted-foreground",
            className
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(true);
          }}
          {...props}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? value : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[9999]" align="start" sideOffset={4}>
        <Calendar
          mode="single"
          selected={dateValue}
          defaultMonth={dateValue || defaultMonthDate}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}
